import React from "react";
import { FiDownload } from "react-icons/fi";
import AccountSidebar from "./AccountSidebar";
import usePaymentHistory from "../../hooks/usePaymentHistory";

function downloadInvoice(payment) {
    const promo =
        payment.raw_response?.promo_code ??
        payment.paystack_payload?.promo_code;
    const lines = [
        "Doctors' Jobs — Payment receipt",
        "",
        `Invoice ID: ${payment.id}`,
        `Date: ${new Date(payment.created_at).toLocaleString()}`,
        `Paystack reference: ${payment.paystack_reference || "—"}`,
        `Amount: ${payment.currency || "ZAR"} ${Number(payment.amount ?? payment.amount_paid).toFixed(2)}`,
        `Status: ${payment.status}`,
        `Job listings purchased: ${payment.listing_count ?? "—"}`,
    ];
    if (promo) lines.push(`Promo code: ${promo}`);
    lines.push("", "Thank you for your purchase.");

    const blob = new Blob([lines.join("\n")], {
        type: "text/plain;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `invoice-${String(payment.id).slice(0, 8)}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

const PaymentHistory = () => {
    const { paymentHistory, loading, error, isEmpty } = usePaymentHistory();

    const formatDate = (dateString) => {
        if (!dateString) return "—";
        return new Date(dateString).toLocaleDateString("en-US", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const formatAmount = (amount, currency = "ZAR") => {
        const sym = currency === "ZAR" ? "R" : `${currency} `;
        return `${sym}${Number(amount).toFixed(2)}`;
    };

    const getStatusText = (status) => {
        if (!status) return "—";
        switch (status.toLowerCase()) {
            case "completed":
            case "success":
            case "succeeded":
            case "paid":
                return "Paid";
            case "failed":
            case "error":
                return "Payment failed";
            default:
                return status;
        }
    };

    const getStatusBadge = (status) => {
        const statusText = getStatusText(status);
        const baseClasses =
            "inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full";
        switch (statusText) {
            case "Paid":
                return `${baseClasses} bg-[#E6EBEA] text-[#064A35]`;
            case "Payment failed":
                return `${baseClasses} bg-red-100 text-red-700`;
            default:
                return `${baseClasses} bg-gray-100 text-gray-600`;
        }
    };

    return (
        <div className="max-w-6xl mx-auto p-4 sm:p-6 flex flex-col md:flex-row gap-8 items-start">
            <AccountSidebar />

            <div className="flex-1 min-w-0 w-full">
                <h2 className="font-bold text-xl mb-6 text-gray-900">
                    Payment history
                </h2>

                {error && (
                    <div className="mb-4 p-3 rounded-md bg-red-50 text-red-800 border border-red-100 text-sm">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="rounded-xl border border-gray-200 overflow-hidden bg-white">
                        <div className="hidden sm:block">
                            {[1, 2, 3].map((i) => (
                                <div
                                    key={i}
                                    className="animate-pulse h-12 bg-gray-50 border-b border-gray-100 last:border-0"
                                />
                            ))}
                        </div>
                        <div className="sm:hidden space-y-3 p-3">
                            {[1, 2, 3].map((i) => (
                                <div
                                    key={i}
                                    className="animate-pulse h-24 bg-gray-100 rounded-lg"
                                />
                            ))}
                        </div>
                    </div>
                ) : isEmpty ? (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <div className="py-16 text-center text-gray-500 px-4">
                            <svg
                                className="w-12 h-12 text-gray-300 mx-auto mb-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                            </svg>
                            <h3 className="text-sm font-medium text-gray-900 mb-1">
                                No payment history
                            </h3>
                            <p className="text-xs text-gray-500 max-w-md mx-auto">
                                You have not made any job listing purchases yet.
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Desktop — match employer dashboard jobs table */}
                        <div className="hidden sm:block overflow-hidden rounded-xl border border-gray-200 bg-white employer-dashboard-table">
                            <table className="w-full text-xs table-fixed">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-2 py-2 text-left text-xs font-normal text-gray-500 w-1/5">
                                            Date
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-normal text-gray-500 w-2/5">
                                            Amount
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-normal text-gray-500 w-24">
                                            Status
                                        </th>
                                        {/* <th className="px-4 py-2 text-right text-xs font-normal text-gray-500 w-28">
                                            Invoice
                                        </th> */}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {paymentHistory.map((payment) => (
                                        <tr
                                            key={payment.id}
                                            className="hover:bg-gray-50"
                                        >
                                            <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                                                {formatDate(payment.created_at)}
                                            </td>
                                            <td className="px-4 py-2">
                                                <div className="text-xs font-medium text-gray-900 break-words">
                                                    {formatAmount(
                                                        payment.amount,
                                                        payment.currency
                                                    )}
                                                </div>
                                                {(payment.raw_response
                                                    ?.promo_code ||
                                                    payment.paystack_payload
                                                        ?.promo_code) && (
                                                    <div className="text-[10px] text-gray-500 mt-0.5">
                                                        Promo:{" "}
                                                        {payment.raw_response
                                                            ?.promo_code ||
                                                            payment
                                                                .paystack_payload
                                                                ?.promo_code}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 whitespace-nowrap">
                                                <span
                                                    className={getStatusBadge(
                                                        payment.status
                                                    )}
                                                >
                                                    {getStatusText(
                                                        payment.status
                                                    )}
                                                </span>
                                            </td>
                                            {/* <td className="px-4 py-2 whitespace-nowrap text-right">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        downloadInvoice(
                                                            payment
                                                        )
                                                    }
                                                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-gray-300 text-xs font-semibold text-gray-800 bg-white hover:bg-gray-50 transition-colors"
                                                >
                                                    <FiDownload
                                                        className="flex-shrink-0 w-3.5 h-3.5"
                                                        aria-hidden
                                                    />
                                                    Download
                                                </button>
                                            </td> */}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile — match employer dashboard card list */}
                        <div className="sm:hidden space-y-3 employer-dashboard-card-list">
                            {paymentHistory.map((payment) => (
                                <div
                                    key={payment.id}
                                    className="bg-white border border-gray-200 rounded-lg p-4 employer-dashboard-mobile-card"
                                >
                                    <div className="flex items-start justify-between gap-3 mb-3">
                                        <div>
                                            <p className="text-xs text-gray-500 mb-0.5">
                                                Date
                                            </p>
                                            <p className="text-sm font-medium text-gray-900">
                                                {formatDate(
                                                    payment.created_at
                                                )}
                                            </p>
                                        </div>
                                        <span
                                            className={getStatusBadge(
                                                payment.status
                                            )}
                                        >
                                            {getStatusText(payment.status)}
                                        </span>
                                    </div>
                                    <div className="mb-3">
                                        <p className="text-xs text-gray-500 mb-0.5">
                                            Amount
                                        </p>
                                        <p className="text-sm font-medium text-gray-900">
                                            {formatAmount(
                                                payment.amount,
                                                payment.currency
                                            )}
                                        </p>
                                        {(payment.raw_response?.promo_code ||
                                            payment.paystack_payload
                                                ?.promo_code) && (
                                            <p className="text-xs text-gray-500 mt-1">
                                                Promo:{" "}
                                                {payment.raw_response
                                                    ?.promo_code ||
                                                    payment.paystack_payload
                                                        ?.promo_code}
                                            </p>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            downloadInvoice(payment)
                                        }
                                        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full border border-gray-300 text-xs font-semibold text-gray-800 bg-gray-50 hover:bg-gray-100 transition-colors"
                                    >
                                        <FiDownload
                                            className="flex-shrink-0"
                                            aria-hidden
                                        />
                                        Download invoice
                                    </button>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default PaymentHistory;
